/**
 * Feature Flag System
 *
 * Centralized feature flag definitions and utilities.
 * Feature flags allow the "quantfolio" tenant to enable/disable
 * features for other tenants.
 */

export const FEATURE_FLAGS = {
  MORNINGSTAR_XRAY: "morningstar_xray",
  MORNINGSTAR_DATA: "morningstar_data",
  MORNINGSTAR_INDEX: "morningstar_index",
  TWELVE_DATA: "twelve_data",
  EODHD: "eodhd",
  PARTNER: "partner",
  SSO: "sso",
  COST_OVERRIDES: "cost_overrides",
  // Add more feature flags here as needed (up to 10-15 total)
} as const;

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

export const FEATURE_FLAG_METADATA: Record<
  FeatureFlagKey,
  {
    name: string;
    description: string;
    category?: string;
  }
> = {
  [FEATURE_FLAGS.MORNINGSTAR_XRAY]: {
    name: "Morningstar X-Ray",
    description: "Enable Morningstar portfolio X-Ray analysis in flows and PDFs",
    category: "Data vendors",
  },
  [FEATURE_FLAGS.MORNINGSTAR_DATA]: {
    name: "Morningstar Data",
    description:
      "Enable access to Morningstar market & reference data — 3 datasets: Funds, Categories and ETFs.",
    category: "Data vendors",
  },
  [FEATURE_FLAGS.MORNINGSTAR_INDEX]: {
    name: "Morningstar Indexes",
    description: "Enable access to Morningstar index data (DWS-licensed indexes).",
    category: "Data vendors",
  },
  [FEATURE_FLAGS.TWELVE_DATA]: {
    name: "Twelve Data",
    description: "Enable access to Twelve Data market data.",
    category: "Data vendors",
  },
  [FEATURE_FLAGS.EODHD]: {
    name: "EODHD",
    description: "Enable access to EOD Historical Data (EODHD) market data.",
    category: "Data vendors",
  },
  [FEATURE_FLAGS.PARTNER]: {
    name: "Partner",
    description: "Enable tenant management. Partner can create and manage sub-tenants.",
    category: "Admin",
  },
  [FEATURE_FLAGS.SSO]: {
    name: "SSO / OIDC",
    description:
      "Enable Single Sign-On via OIDC. Allows users to sign in with external identity providers.",
    category: "Authentication",
  },
  [FEATURE_FLAGS.COST_OVERRIDES]: {
    name: "Advisor cost overrides",
    description:
      "Let advisors override collection fees within one advice session. Which fees are overridable, and whether a note is required, is set per fee in the collection's Costs settings.",
    category: "Advice",
  },
};
